import { Box, Heading, SimpleGrid, VStack } from "@chakra-ui/react";
import { FragmentType, getFragmentData, gql } from "__generated__/gql";
import Address from "components/Shared/Address";
import EmailLink from "components/Shared/EmailLink";
import { NA } from "components/Shared/EmptyPlaceholder";
import ExternalDomainLink from "components/Shared/ExternalDomainLink";
import LabeledValue from "components/Shared/LabeledValue";
import LabeledValueGrid from "components/Shared/LabeledValueGrid";
import PhoneNumberLink from "components/Shared/PhoneNumberLink";
import AttributeTagList from "components/Shared/AttributeTagList";
import ContactViewSection from "./ContactViewSection";

const fragment = gql(/* GraphQL */ `
  fragment BrandSettingsCompanyInformationViewFields on Brand {
    id
    legalName
    website
    headquartersContact {
      id
      email
      address {
        id
        ...AddressComponentFields
      }
      phoneNumber {
        id
        ...PhoneNumberLinkFields
      }
    }
    billingContact {
      id
      ...BrandSettingsCompanyInformationContactViewSectionFields
    }
    ordersLogisticsContact {
      id
      ...BrandSettingsCompanyInformationContactViewSectionFields
    }
    companyAttributesList(orderBy: NAME_ASC) {
      id
      ...AttributeTagListAttribute
    }
  }
`);

type Props = {
  brand: FragmentType<typeof fragment> | null | undefined;
  isLoading?: boolean;
};

export default function BrandSettingsCompanyInformationView({
  brand: brandFragmentData,
  isLoading,
}: Props) {
  const brand = getFragmentData(fragment, brandFragmentData);
  const companyAttributes = brand?.companyAttributesList || [];

  return (
    <SimpleGrid
      gridTemplateColumns="repeat(auto-fit, minmax(15rem, 1fr))"
      gap={10}
    >
      <Box>
        <LabeledValue label="Legal Name" value={brand?.legalName} />
        <LabeledValue
          label="Website"
          value={
            brand?.website && (
              <ExternalDomainLink href={brand.website} hasIcon />
            )
          }
        />
        <LabeledValue
          label="Certifications & Attributes"
          value={
            companyAttributes.length > 0 ? (
              <AttributeTagList
                items={companyAttributes}
                borderColor="gray.400"
                borderWidth={1}
                color="gray.600"
              />
            ) : null
          }
        />

        <Box mt={8}>
          <Heading as="h3" size="sm" color="magenta.veryDark" mb={4}>
            Headquarters
          </Heading>
          <LabeledValueGrid isLoading={isLoading}>
            <LabeledValue
              label="Email"
              value={<EmailLink email={brand?.headquartersContact?.email} />}
              isValuePresent={!!brand?.headquartersContact?.email}
            />
            <LabeledValue
              label="Phone"
              value={
                <PhoneNumberLink
                  phoneNumber={brand?.headquartersContact?.phoneNumber}
                />
              }
              isValuePresent={!!brand?.headquartersContact?.phoneNumber}
            />
            <LabeledValue
              label="Address"
              value={
                <Address
                  address={brand?.headquartersContact?.address}
                  fallback={<NA />}
                />
              }
            />
          </LabeledValueGrid>
        </Box>
      </Box>

      <VStack alignItems="stretch" spacing={4}>
        <ContactViewSection
          title="Billing Contact"
          contact={brand?.billingContact}
          isLoading={isLoading}
        />
        <ContactViewSection
          title="Orders & Logistics Contact"
          contact={brand?.ordersLogisticsContact}
          isLoading={isLoading}
        />
      </VStack>
    </SimpleGrid>
  );
}
